import { Button } from '@design-system/components/button/button';
import { Input } from '@design-system/components/input/input';
import { Modal, PlainModalProps } from '@design-system/components/modal/modal';
import { Typography } from '@design-system/components/typography/typography';
import { useState } from 'react';
import SubscriptionInfoImg from './offer-subscription-info.png';
import { Tag } from '@design-system/components/tag/tag';
import OtpSelectionImg from './guide-otp-type.png';
import SubscriptionSelectionImg from './guide-subscription-type.png';
import { InitialCollection } from '../../home.viewModel';
import { clsx } from 'clsx';
import { CollectionType, SubscriptionType } from '../../home.domain';
import { ExclusivePriceSetupProps } from '@client/new/common/selfServeExclusive/exclusivePriceSetup';
import { SubscriptionPriceSelection } from './SubscriptionPriceSelection';
import { StepCount } from './StepCount';
import { SubscriptionSummary } from './SubscriptionSummary';
import { Summary } from './Summary';

type OfferModalProps = Pick<PlainModalProps, 'isOpen' | 'onClose'> & {
  collection: InitialCollection;
  setName: (value: string) => void;
  setType: (type: CollectionType) => void;
  createOffer: () => void;
  submitting: boolean;
  videoPlayed: (url: string) => void;
  offerTypeSelected: (type: CollectionType) => void;
  subscriptionTypeSelected: (type: SubscriptionType) => void;
  exclusive: ExclusivePriceSetupProps;
  savePricing: (callback?: () => void) => void;
  errors?: string[];
};

export const OfferModal = (props: OfferModalProps) => (
  <Modal
    {...props}
    modalClassName="w-full h-full md:w-[80%] md:h-[80%]"
    contentClassName="w-full h-full gap-0"
    disableBackdropClick
  >
    <Content {...props} />
  </Modal>
);

type OfferStep =
  | 'ProductName'
  | 'OtpInfo'
  | 'SubscriptionInfo'
  | 'OfferType'
  | 'SubscriptionPrice'
  | 'SubscriptionSummary'
  | 'Summary';

const Content = (props: OfferModalProps) => {
  const [step, setStep] = useState<OfferStep>('ProductName');

  switch (step) {
    case 'ProductName':
      return (
        <ProductName
          next={() => setStep('OtpInfo')}
          setName={props.setName}
          collection={props.collection}
        />
      );
    case 'OtpInfo':
      return (
        <OtpInfo
          next={() => setStep('SubscriptionInfo')}
          back={() => setStep('ProductName')}
        />
      );
    case 'SubscriptionInfo':
      return (
        <SubscriptionInfo
          next={() => setStep('OfferType')}
          back={() => setStep('OtpInfo')}
        />
      );
    case 'OfferType': {
      const nextStep =
        props.collection.type === 'subscription'
          ? 'SubscriptionPrice'
          : 'Summary';
      return (
        <OfferType
          next={() => {
            props.offerTypeSelected(props.collection.type);
            setStep(nextStep);
          }}
          back={() => setStep('SubscriptionInfo')}
          type={props.collection.type}
          setType={props.setType}
        />
      );
    }
    case 'SubscriptionPrice':
      return (
        <SubscriptionPriceSelection
          next={() => {
            props.collection.subscriptionType &&
              props.subscriptionTypeSelected(props.collection.subscriptionType);
            setStep('SubscriptionSummary');
          }}
          back={() => setStep('OfferType')}
          max={7}
          step={5}
          savePricing={props.savePricing}
          errors={props.errors}
          {...props.exclusive}
        />
      );
    case 'SubscriptionSummary': {
      return (
        <SubscriptionSummary
          next={() => setStep('Summary')}
          back={() => setStep('SubscriptionPrice')}
          max={7}
          step={6}
          monthly={props.exclusive.monthly}
          annual={props.exclusive.annual}
        />
      );
    }
    case 'Summary': {
      const backStep =
        props.collection.type === 'subscription'
          ? 'SubscriptionSummary'
          : 'OfferType';
      return (
        <Summary
          next={() => props.createOffer()}
          back={() => setStep(backStep)}
          version={props.collection.type}
          submitting={props.submitting}
          step={props.collection.type === 'otp' ? 5 : 7}
          max={props.collection.type === 'otp' ? 5 : 7}
        />
      );
    }
    default:
      return (
        <ProductName
          next={() => setStep('ProductName')}
          setName={props.setName}
          collection={props.collection}
        />
      );
  }
};

const ProductName = ({
  next,
  setName,
  collection,
}: {
  next: () => void;
  setName: (value: string) => void;
  collection: InitialCollection;
}) => {
  const [error, setError] = useState<string | undefined>(undefined);
  const onNext = () => {
    if (!collection.name?.trim()) {
      setError('Name is required');
      return;
    }
    next();
  };
  return (
    <div className="flex flex-col w-full h-full">
      <StepCount step={1} />
      <div className="flex flex-col items-center justify-center gap-[23px] w-full h-full">
        <Typography variant="headline" size="large" className="text-center">
          Name your product
        </Typography>
        <div className="flex flex-col items-center justify-center">
          <Typography className="text-center" variant="body" size="large">
            We recommend calling out an amount of time and a goal.
          </Typography>
          <Typography className="text-center" variant="body" size="large">
            For example: “Two week Beach Body” “Beginner Strength & Power”
          </Typography>
        </div>
        <Input
          wrapperClassName="max-w-[50%]"
          value={collection.name}
          onChange={(ev) => {
            error && setError(undefined);
            setName(ev.target.value);
          }}
          error={error}
        />
        <Typography variant="body" size="medium" fontWeight="normal">
          No worries , you'll be able to change this later.
        </Typography>
        <Typography variant="body" size="large">
          Next, we'll teach you about 2 key ways you can sell on Playbook 👉
        </Typography>
      </div>
      <div className="flex justify-end">
        <Button color="neutral" onClick={onNext}>
          Continue
        </Button>
      </div>
    </div>
  );
};

const OtpSlotTag = ({ label }: { label: string }) => (
  <Tag label={label} readonly={true} className="bg-[#EDE5FC]" />
);

const otpTypes = [
  {
    title: '🍪',
    subtitle: 'Lead Gen',
    tags: [
      { label: '$0-5' },
      {
        label: '1-2 weeks of programs',
      },
    ],
    details:
      'Good for testing your audience to see how interested they are in purchasing your programs.',
  },
  {
    title: '🌱',
    subtitle: 'Low-Ticket',
    tags: [
      { label: '$5-50' },
      {
        label: '2-4 weeks of programs',
      },
    ],
    details: 'This is a quality program that your consumers are eager for.',
  },
  {
    title: '🧠',
    subtitle: 'High-Ticket',
    tags: [
      { label: '$50+' },
      {
        label: '6+ weeks of programs',
      },
    ],
    details:
      'In-depth programs targeting a specific audience who knows exactly what they are purchasing and understand the value.',
  },
];

const OtpInfo = ({ next, back }: { next: () => void; back: () => void }) => (
  <div className="flex flex-col w-full h-full">
    <StepCount step={2} />
    <div className="flex flex-col items-center justify-start md:justify-center gap-[23px] w-full h-[100vh] overflow-y-scroll md:overflow-y-auto">
      <Typography variant="headline" size="large" className="text-center">
        Try a One-Time Purchase to start!
      </Typography>
      <div className="flex flex-col items-center justify-center max-w-[680px]">
        <Typography className="text-center" variant="body" size="large">
          We want you to have a great expectation of how much you'll need to
          create so that you're selling something worthwhile to your customers.
        </Typography>
        <br />
        <Typography className="text-center" variant="body" size="large">
          Have a look at these guidelines before moving forward.
        </Typography>
      </div>
      <div className="flex flex-col md:flex-row gap-4 overflow-auto">
        {otpTypes.map(({ title, subtitle, tags, details }) => (
          <div
            key={title}
            className="flex flex-col gap-4 rounded-lg w-[300px] items-center bg-[#F8F8F8] p-3"
          >
            <Typography variant="headline" size="medium">
              {title}
            </Typography>
            <Typography variant="body" size="large">
              {subtitle}
            </Typography>
            <div className="flex gap-2">
              {tags.map(({ label }) => (
                <OtpSlotTag label={label} key={label} />
              ))}
            </div>
            <Typography
              className="text-center"
              variant="body"
              size="medium"
              fontWeight="normal"
            >
              {details}
            </Typography>
          </div>
        ))}
      </div>
    </div>
    <div className="flex justify-between pt-2">
      <Button variant="text" color="neutral" onClick={back}>
        Back
      </Button>
      <Button color="neutral" onClick={next}>
        Next
      </Button>
    </div>
  </div>
);

const SubscriptionInfo = ({
  next,
  back,
}: {
  next: () => void;
  back: () => void;
}) => (
  <div className="flex flex-col w-full h-full">
    <StepCount step={3} />
    <div className="flex flex-col items-center justify-center gap-[23px] w-full h-full">
      <Typography variant="headline" size="large" className="text-center">
        Thinking about a Subscription?
      </Typography>
      <div className="flex flex-col items-center justify-center max-w-[680px]">
        <Typography className="text-center" variant="body" size="large">
          The payoff may be larger, but so is your upfront investment. Only
          start with a Subscription if you are ready to create a few months
          worth of content.
        </Typography>
      </div>
      <img
        src={SubscriptionInfoImg}
        className="w-full h-auto md:max-w-[740px] md:max-h-[400px]"
      />
    </div>
    <div className="flex justify-between">
      <Button variant="text" color="neutral" onClick={back}>
        Back
      </Button>
      <Button color="neutral" onClick={next}>
        Next
      </Button>
    </div>
  </div>
);

/* deprecated - do not remove until exclusive subscription only decision is final */
// const SubscriptionTypeSelection = ({
//   next,
//   back,
//   options,
//   type,
// }: {
//   next: () => void;
//   back: () => void;
//   options: SubscriptionTypeOptions;
//   type?: 'ppc' | 'platform';
// }) => {
//   const [error, setError] = useState<string | undefined>(undefined);

//   useEffect(() => {
//     if (type && error) {
//       setError(undefined);
//     }
//   }, [error, type]);

//   const onNext = () => {
//     if (!type) {
//       setError('Please select business model');
//       return;
//     }
//     next();
//   };
//   return (
//     <div className="flex flex-col w-full h-full">
//       <StepCount step={4} />
//       <div className="flex flex-col items-center justify-start md:justify-center gap-[23px] w-full h-[100vh] overflow-y-scroll md:overflow-y-auto">
//         <Typography
//           variant="headline"
//           size="large"
//           className="text-center pt-6 md:pt-0"
//         >
//           Exclusive Subscription
//         </Typography>
//         <Typography
//           className="text-center max-w-[630px]"
//           variant="body"
//           size="large"
//         >
//           With a pay-per-creator model, you set your own subscription prices.
//           Your content isn’t bundled into a platform-wide subscription. It's all
//           about you!
//         </Typography>
//         <div className="flex flex-col md:flex-row gap-6">
//           {options.map((option) => (
//             <div
//               key={option.title}
//               className={clsx(
//                 'flex flex-col gap-2.5 items-center rounded-[10px] border-4 shadow-gallery w-[338px] h-[380px] px-4 py-2 cursor-pointer bg-[#F8F8F8]',
//                 type !== option.type && 'border-transparent',
//                 type === option.type && 'border-black',
//                 option.disabled &&
//                   'pointer-events-none bg-white cursor-not-allowed',
//               )}
//               onClick={option.onClick}
//             >
//               <div className="flex flex-col gap-2">
//                 <div className="flex gap-2 items-center">
//                   <Typography variant="headline" size="small">
//                     {option.title}
//                   </Typography>
//                   {option.tag && (
//                     <Tag
//                       label={option.tag.label}
//                       readonly={option.tag.readonly}
//                       className={option.tag.className}
//                     />
//                   )}
//                 </div>
//                 <Typography variant="body" size="large">
//                   {option.details}
//                 </Typography>
//               </div>
//               <div className="flex flex-col gap-4 flex-grow">
//                 {option.list.map((item) => (
//                   <div key={item.details} className="flex flex-col gap-1">
//                     <div className="flex items-center gap-2">
//                       <FontAwesomeIcon icon={item.icon} className="w-[20px]" />
//                       <Typography variant="body" size="large">
//                         {item.title}
//                       </Typography>
//                     </div>
//                     <Typography
//                       variant="body"
//                       size="medium"
//                       fontWeight="normal"
//                       className={clsx('pl-7', sprinkles({ color: 'N-80' }))}
//                     >
//                       {item.details}
//                     </Typography>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           ))}
//         </div>
//         <a
//           href="https://help.playbookapp.io/en/articles/9698798-how-the-exclusive-subscription-model-works"
//           target="_blank"
//           rel="noreferrer"
//         >
//           <Typography
//             className="text-center cursor-pointer p-4"
//             variant="body"
//             size="large"
//             color="P-80"
//           >
//             Learn more
//           </Typography>
//         </a>
//         <Typography
//           color="ERROR-100"
//           variant="body"
//           size="large"
//           className="text-center"
//         >
//           {error}
//         </Typography>
//       </div>
//       <div className="flex justify-between pt-2">
//         <Button variant="text" color="neutral" onClick={back}>
//           Back
//         </Button>
//         <Button color="neutral" onClick={onNext}>
//           Select
//         </Button>
//       </div>
//     </div>
//   );
// };

const OfferType = ({
  next,
  back,
  type,
  setType,
}: {
  next: () => void;
  back: () => void;
  type: CollectionType;
  setType: (type: CollectionType) => void;
}) => (
  <div className="flex flex-col w-full h-full">
    <StepCount step={4} />
    <div className="flex flex-col items-center justify-start md:justify-center gap-[23px] w-full h-[100vh] overflow-y-scroll md:overflow-y-auto">
      <Typography variant="headline" size="large" className="text-center">
        Choose which product type you'd like to start with
      </Typography>
      <div className="flex flex-col md:flex-row gap-6">
        <div
          className={clsx(
            'flex flex-col gap-2.5 items-center rounded-[10px] border-4 shadow-gallery w-[338px] h-[380px] px-4 py-2',
            type !== 'otp' && 'border-transparent',
            type === 'otp' && 'border-black',
          )}
          onClick={() => setType('otp')}
        >
          <Typography variant="title">One-Time Purchase</Typography>
          <img src={OtpSelectionImg} className="max-w-[180px] max-h-[280px]" />
          <div className="rounded-[30px] bg-[#4BE589] py-1 px-4">
            <Typography variant="body" size="small" fontWeight="bold">
              GREAT FOR GETTING STARTED 🔥
            </Typography>
          </div>
          <Typography variant="body" size="medium" fontWeight="normal">
            Your customer pays once
          </Typography>
          <a
            href="https://help.playbookapp.io/en/articles/9655540-one-time-purchases-create-a-high-converting-landing-page"
            target="_blank"
            rel="noreferrer"
          >
            <Typography
              className="text-center cursor-pointer p-4"
              variant="body"
              size="large"
              color="P-80"
            >
              Learn more
            </Typography>
          </a>
        </div>
        <div
          className={clsx(
            'flex flex-col gap-2.5 items-center rounded-[10px] border-4 shadow-gallery w-[338px] h-[380px] px-4 py-2',
            type !== 'subscription' && 'border-transparent',
            type === 'subscription' && 'border-black',
          )}
          onClick={() => setType('subscription')}
        >
          <Typography variant="title">Subscription</Typography>
          <img
            src={SubscriptionSelectionImg}
            className="max-w-[180px] max-h-[280px]"
          />
          <Typography
            variant="body"
            size="medium"
            fontWeight="normal"
            className="pt-6"
          >
            Receive recurring payments from consumers
          </Typography>
          <a
            href="https://help.playbookapp.io/en/articles/9693285-subscription-business-what-a-successful-experience-looks-like"
            target="_blank"
            rel="noreferrer"
          >
            <Typography
              className="text-center cursor-pointer p-4"
              variant="body"
              size="large"
              color="P-80"
            >
              Learn more
            </Typography>
          </a>
        </div>
      </div>
      <Typography
        className="text-center max-w-[680px]"
        variant="body"
        size="large"
        fontWeight="normal"
      >
        Don't worry, you can always offer more products with Playbook.{' '}
        <b>
          To get started as quickly as possible, consider building a One-time
          Purchase product
        </b>
        . It's the least amount of investment and can allow you to test your
        market.
      </Typography>
    </div>
    <div className="flex justify-between pt-2">
      <Button variant="text" color="neutral" onClick={back}>
        Back
      </Button>
      <Button color="neutral" onClick={next}>
        Select
      </Button>
    </div>
  </div>
);
